import { ExternalLink } from "lucide-react";
import { useEntitlements } from "@fox1ck-org/auth/saas/react";
import { Button, ProgressBar, cn } from "@fox1ck-org/ui";

import { formatInt } from "@/lib/format";
import { useT } from "@/lib/i18n";
import { links, meterLabel, meterUsage, type MeterUsage, type TrackerMeter } from "@/lib/plan";
import { useCampaignCount } from "@/lib/services/campaign-service";
import { useListTrackingDomains } from "@/lib/services/tracking-domain-service";

/**
 * The org's usage of `meter` against its plan: the limit is console's
 * entitlements, the count the rail's own cached query — the same the server's
 * `admit` counts (campaigns not archived, every tracking domain).
 */
export function useMeterUsage(meter: TrackerMeter): MeterUsage {
  const ent = useEntitlements();
  const campaigns = useCampaignCount({ enabled: meter === "tracker.campaigns" });
  const domains = useListTrackingDomains({ limit: 1 }, { enabled: meter === "tracker.domains" });
  const used = meter === "tracker.campaigns" ? campaigns.data : domains.data?.total;
  return meterUsage(meter, used, ent.limit(meter), ent.isLoading);
}

/**
 * «Домены: 3 из 10 по тарифу · Увеличить» (card S11b): a hard meter of the
 * org's plan against what the org uses, above a list. Drawn only for a finite
 * ceiling — an unlimited plan (our own org «Vibe») shows nothing. Never red:
 * at the ceiling the line says so in warn, and «Увеличить» leads to
 * console's billing for that meter.
 */
export function PlanLimit({ meter, className }: { meter: TrackerMeter; className?: string }) {
  const usage = useMeterUsage(meter);
  return <PlanLimitView usage={usage} className={className} />;
}

/** The line itself, for a known usage. */
export function PlanLimitView({ usage, className }: { usage: MeterUsage; className?: string }) {
  const t = useT();
  if (!usage.limited || usage.used === undefined) return null;
  const limit = usage.limit as number;
  const meter = meterLabel(usage.meter);
  const vars = { meter, used: formatInt(usage.used), limit: formatInt(limit) };
  return (
    <div
      className={cn("flex shrink-0 items-center gap-3 rounded-lg border border-border bg-card px-3.5 py-2", className)}
      role="status"
      data-testid={`plan-limit-${usage.meter}`}
    >
      <span className={cn("shrink-0 text-sm", usage.full ? "font-medium" : "text-muted-foreground")}>{t(usage.full ? "plan.usage.full" : "plan.usage", vars)}</span>
      <ProgressBar value={usage.used} max={Math.max(limit, 1)} size="xs" tone={usage.full ? "warn" : "primary"} showPercent={false} className="min-w-24 max-w-60 flex-1" />
      <Button asChild variant={usage.full ? "primary" : "secondary"} size="sm" className="ml-auto shrink-0">
        <a href={links.billing({ meter: usage.meter })} target="_blank" rel="noreferrer">
          {t("plan.increase")} <ExternalLink className="size-3.5" />
        </a>
      </Button>
    </div>
  );
}
